import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  GuardrailsSettings,
  PropertyPortfolioChangePreview,
  PropertyProfile,
  PropertySummary
} from '@app/core/models';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class PropertiesApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/properties`;

  getProperties() {
    return this.http.get<PropertySummary[]>(this.baseUrl);
  }

  getProperty(propertyId: string) {
    return this.http.get<PropertyProfile>(`${this.baseUrl}/${propertyId}`);
  }

  createProperty(payload: PropertyProfile) {
    return this.http.post<PropertyProfile>(this.baseUrl, payload);
  }

  updateProperty(propertyId: string, payload: PropertyProfile) {
    return this.http.put<PropertyProfile>(`${this.baseUrl}/${propertyId}`, payload);
  }

  updateLifecycle(propertyId: string, lifecycleStatus: PropertyProfile['lifecycleStatus']) {
    return this.http.patch<PropertyProfile>(`${this.baseUrl}/${propertyId}/lifecycle`, {
      lifecycleStatus
    });
  }

  previewLifecycleChange(propertyId: string, lifecycleStatus: PropertyProfile['lifecycleStatus']) {
    return this.http.post<PropertyPortfolioChangePreview>(
      `${this.baseUrl}/${propertyId}/lifecycle-change/preview`,
      { lifecycleStatus }
    );
  }

  previewDeleteChange(propertyId: string) {
    return this.http.post<PropertyPortfolioChangePreview>(
      `${this.baseUrl}/${propertyId}/delete-change/preview`,
      {}
    );
  }

  previewPmsSync(selectedExternalPropertyIds: string[] = []) {
    return this.http.post<PropertyPortfolioChangePreview>(`${this.baseUrl}/pms-sync/preview`, {
      selectedExternalPropertyIds
    });
  }

  applyPortfolioChange(changeId: string) {
    return this.http.post<PropertyPortfolioChangePreview>(
      `${this.baseUrl}/portfolio-changes/${changeId}/apply`,
      {}
    );
  }

  validateConfiguration(propertyId: string) {
    return this.http.post<PropertyProfile>(`${this.baseUrl}/${propertyId}/validate-configuration`, {});
  }

  deleteProperty(propertyId: string) {
    return this.http.delete<{ success: true }>(`${this.baseUrl}/${propertyId}`);
  }

  getGuardrails(propertyId: string) {
    return this.http.get<GuardrailsSettings>(`${this.baseUrl}/${propertyId}/guardrails`);
  }

  updateGuardrails(propertyId: string, payload: GuardrailsSettings) {
    return this.http.put<GuardrailsSettings>(`${this.baseUrl}/${propertyId}/guardrails`, payload);
  }
}
